import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Shield,
    Users,
    Award,
    HeartHandshake,
    Compass,
    Star,
    Sparkles,
    Briefcase,
    ArrowRight,
    Building2,
    User,
} from "lucide-react";
import { IMG, BTN, BRAND } from "@/lib/design";

const fade = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const VALUES = [
    { icon: HeartHandshake, title: "Connection", body: "Healthy relationships are central to healing. We focus on trust-building, respectful communication, and consistent adult support." },
    { icon: Award, title: "Accountability", body: "Young men need structure, boundaries, and clear expectations. Accountability is delivered with dignity, not shame." },
    { icon: Shield, title: "Stability", body: "We provide a physically, emotionally, and relationally safe environment where youth can stabilize, process, and grow." },
    { icon: Sparkles, title: "Growth", body: "We measure success by who a young man is becoming — emotionally, academically, relationally, and personally." },
    { icon: Compass, title: "Integrity", body: "We operate with honesty, transparency, professionalism, and responsibility to youth, families, caregivers, DFPS, courts, and community partners." },
    { icon: Star, title: "Compassion", body: "We recognize behavior is communication. We respond to trauma, grief, fear, and survival with patience, understanding, and steady firmness when needed." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="relative bg-[#F7F5F0] pt-16 md:pt-24 pb-12">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        About Crossroads to Change
                    </p>
                    <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1A2230] leading-[1.05]">
                        Every young man deserves a real chance.
                    </h1>
                    <p className="mt-6 text-[17px] leading-relaxed text-[#6F6B63] max-w-3xl">
                        At Crossroads to Change, we believe behavior is
                        communication. Many youth entering residential care
                        have experienced trauma, instability, rejection,
                        placement disruptions, or unmet emotional needs. Our
                        role is not to punish pain. Our role is to create
                        structure, safety, and accountability so healing can
                        begin.
                    </p>
                </div>
            </section>

            {/* IMAGE BAND */}
            <section className="py-10 bg-[#F7F5F0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                    <div className="relative aspect-[21/9] rounded-3xl overflow-hidden">
                        <img
                            src={IMG.community}
                            alt="Community of hands joined together"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="about-banner-image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A47]/40 to-transparent" />
                    </div>
                </div>
            </section>

            {/* STORY */}
            <section className="py-20 md:py-24 bg-[#F7F5F0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <motion.div {...fade} className="md:col-span-5">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            Our story
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            More than a placement — a steady place to heal.
                        </h2>
                    </motion.div>
                    <motion.div {...fade} className="md:col-span-7 space-y-5 text-[16px] leading-relaxed text-[#1A2230]/85">
                        <p>
                            Crossroads to Change Residential Treatment Center
                            was built for male youth who need more than a
                            placement. They need adults who are calm,
                            consistent, trained, and willing to help them
                            work through hard moments without giving up on
                            them.
                        </p>
                        <p>
                            We provide a home-like setting with strong
                            supervision, clear expectations, therapeutic
                            services, and daily opportunities for youth to
                            practice better choices. Through individual
                            therapy, group support, psychiatric care
                            coordination, education partnership, and
                            relationship-based staff connection, we help young
                            men move from survival to stability.
                        </p>
                        <p>
                            We don't promise miracles. We promise consistency,
                            trauma-informed care, and a team that keeps
                            showing up.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* CEO STATEMENT */}
            <section className="relative py-20 md:py-28 bg-[#0E2A47] text-white overflow-hidden" data-testid="ceo-statement">
                <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#C8A24A]/15 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#7BA37F]/10 blur-3xl" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        A message from our CEO
                    </p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
                        <div className="md:col-span-2 flex md:justify-end">
                            <svg
                                aria-hidden
                                className="h-14 w-14 md:h-20 md:w-20 text-[#C8A24A]"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <path d="M9.13 8.74A5.86 5.86 0 0 0 3.27 14.6v2.93h5.86V11.67H6.2a3 3 0 0 1 2.93-2.93v-.0zM20.73 8.74a5.86 5.86 0 0 0-5.86 5.86v2.93h5.86V11.67h-2.93a3 3 0 0 1 2.93-2.93z" />
                            </svg>
                        </div>
                        <div className="md:col-span-10">
                            <blockquote
                                className="font-[Outfit] text-2xl sm:text-3xl md:text-4xl font-medium leading-[1.25] tracking-tight text-white"
                                data-testid="ceo-quote"
                            >
                                "We believe young men deserve more than placement — they deserve the opportunity to heal, grow, and build a stronger future."
                            </blockquote>
                            <div className="mt-8 flex items-center gap-4">
                                <span className="h-11 w-11 rounded-full bg-[#C8A24A] grid place-items-center text-[#0E2A47] font-[Outfit] font-semibold text-[16px]">
                                    DS
                                </span>
                                <div className="leading-tight">
                                    <p className="font-[Outfit] text-[16px] font-semibold text-white" data-testid="ceo-name">
                                        Dorothy Summage &amp; Valerie Payton
                                    </p>
                                    <p className="text-[12.5px] text-white/65 uppercase tracking-[0.16em] mt-1">
                                        Founder &amp; CEO · Crossroads to Change
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CORE VALUES */}
            <section className="py-20 md:py-24 bg-[#EFEBE0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                    <div className="max-w-2xl mb-12">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            Core values
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            The principles behind the work.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {VALUES.map(({ icon: Icon, title, body }) => (
                            <div
                                key={title}
                                className="bento-card bg-white rounded-2xl p-6 border border-[#E5DFD3]"
                                data-testid={`value-${title.toLowerCase()}`}
                            >
                                <div className="h-10 w-10 rounded-xl bg-[#E8EFE8] grid place-items-center text-[#0E2A47] mb-4">
                                    <Icon className="h-4.5 w-4.5" />
                                </div>
                                <h3 className="font-[Outfit] text-[17.5px] font-semibold tracking-tight mb-1.5">
                                    {title}
                                </h3>
                                <p className="text-[14px] leading-relaxed text-[#6F6B63]">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PLACEHOLDERS: FOUNDER, STAFF, PARTNERSHIPS */}
            <section className="py-20 bg-[#F7F5F0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        {
                            icon: User,
                            title: "Founder's Story",
                            body: "Coming soon — a personal note from our founder on what called this program into being and the young men whose stories shaped it.",
                            testid: "founder-placeholder",
                        },
                        {
                            icon: Users,
                            title: "Staff Biographies",
                            body: "Coming soon — meet the mentors, house leaders and partners who carry the daily rhythm of life at Crossroads to Change Residential Treatment Center.",
                            testid: "staff-placeholder",
                        },
                        {
                            icon: Building2,
                            title: "Community Partnerships",
                            body: "Coming soon — the schools, churches, agencies and community organizations that make this work possible.",
                            testid: "partnerships-placeholder",
                        },
                    ].map((p) => (
                        <div
                            key={p.title}
                            className="bento-card bg-white rounded-2xl p-7 border border-[#E5DFD3]"
                            data-testid={p.testid}
                        >
                            <div className="h-11 w-11 rounded-xl bg-[#E8EFE8] grid place-items-center text-[#0E2A47] mb-4">
                                <p.icon className="h-5 w-5" />
                            </div>
                            <h3 className="font-[Outfit] text-[19px] font-semibold tracking-tight mb-2">
                                {p.title}
                            </h3>
                            <p className="text-[14.5px] leading-relaxed text-[#6F6B63]">
                                {p.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* COMPLIANCE & ORGANIZATIONAL */}
            <section className="py-20 bg-[#0E2A47] text-white">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Compliance & organizational information
                    </p>
                    <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight text-white">
                        Operating with integrity and transparency.
                    </h2>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="compliance-ein">
                            <p className="text-[12px] uppercase tracking-wider text-white/60">
                                Federal Tax ID (EIN)
                            </p>
                            <p className="font-[Outfit] text-2xl font-semibold mt-1">
                                {BRAND.ein}
                            </p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="compliance-license">
                            <p className="text-[12px] uppercase tracking-wider text-white/60">
                                State licensing
                            </p>
                            <p className="font-[Outfit] text-[16px] font-medium mt-1">
                                Licensing details available on request.
                            </p>
                        </div>
                    </div>
                    <p className="mt-8 text-[15px] leading-relaxed text-white/80 max-w-3xl">
                        Crossroads to Change Residential Treatment Center is
                        operated in compliance with applicable residential
                        child care licensing standards in the State of Texas.
                        Referrals and admissions are reviewed case-by-case to
                        ensure we can safely and appropriately meet each
                        youth's needs.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-[#F7F5F0]">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                        Have a young man who needs a steady place?
                    </h2>
                    <p className="mt-4 text-[16px] text-[#6F6B63] max-w-2xl mx-auto">
                        Let's talk. Our team will walk you through the
                        referral process, eligibility, and what life looks
                        like inside our home.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/referrals" className={BTN.primary} data-testid="about-refer-cta">
                            Make a referral <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link to="/contact" className={BTN.outline} data-testid="about-contact-cta">
                            Talk with our team
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
